import type {
  FieldCategory,
  FieldValueType,
  FilterFieldConfig,
  FilterOperator,
} from './filterable-fields';
import type { CustomFieldDataType, CustomFieldDefinition } from '../types/custom-field-definitions.types';

const DATA_TYPE_TO_FIELD_SHAPE: Record<
  CustomFieldDataType,
  { valueType: FieldValueType; operators: FilterOperator[] }
> = {
  Text: { valueType: 'string', operators: ['equals', 'contains', 'notEquals', 'isEmpty'] },
  Number: { valueType: 'number', operators: ['equals', 'notEquals', 'isEmpty'] },
  NumberRange: { valueType: 'number', operators: ['numberRange'] },
  Boolean: { valueType: 'boolean', operators: ['equals', 'isEmpty'] },
  Date: { valueType: 'date', operators: ['equals', 'notEquals', 'isEmpty'] },
  Dropdown: { valueType: 'select', operators: ['equals', 'contains', 'notEquals', 'isEmpty'] },
  // A multienum (#231) holds a set, so it offers the set semantics the segment builder does:
  // equals reads "is any of" and notEquals "is none of". `contains` is gone — a substring match
  // against option values is the filter-vs-builder drift the segment grammar ADR records.
  MultiSelect: { valueType: 'multiSelect', operators: ['equals', 'notEquals', 'isEmpty'] },
};

const isKnownDataType = (value: string): value is CustomFieldDataType =>
  value in DATA_TYPE_TO_FIELD_SHAPE;

export const mapCustomPropertyDefinitionToFilterField = (
  definition: CustomFieldDefinition
): FilterFieldConfig | null => {
  const key = definition.Key?.trim();
  const label = definition.Label?.trim();
  const dataType = definition.DataType?.trim();
  const category = definition.Category?.trim();

  if (!key || !label || !dataType || !category) return null;
  if (!isKnownDataType(dataType)) return null;

  const shape = DATA_TYPE_TO_FIELD_SHAPE[dataType];
  const isRangeFilterable = definition.IsRangeFilterable === true;

  // Date defaults: missing/`false` `IsRangeFilterable` → exact operators. Existing Date attributes
  // need `isRangeFilterable: true` from the API (or a backfill) to keep range filtering; until then,
  // saved segment rows holding a `dateRange` operator against a non-range Date field are dropped.
  let operators = shape.operators;
  if (dataType === 'Number') {
    operators = isRangeFilterable ? ['numberRange'] : ['equals', 'notEquals', 'isEmpty'];
  } else if (dataType === 'Date') {
    operators = isRangeFilterable ? ['dateRange'] : ['equals', 'notEquals', 'isEmpty'];
  } else if (dataType === 'NumberRange') {
    operators = ['numberRange'];
  }

  const options = (definition.Options ?? [])
    .filter((option): option is string => Boolean(option?.trim()))
    .map((option) => ({ value: option, label: option }));

  return {
    key,
    label,
    category: category as FieldCategory,
    storageKind: 'customAttribute',
    mongoField: 'Attributes',
    valueType: shape.valueType,
    operators,
    ...(options.length > 0 ? { options } : {}),
  };
};
